import {
  newId,
  nextDue,
  isDueReached,
  appendLog,
  logEntry,
  type TodoMutation,
  type TodoRecurrence,
  type TodoLogAction,
} from '@familyhub/shared';
import { stamp } from '../platform/clock';
import { tdb, type TodoRecord } from './db';
import { todoSync } from './sync';

/**
 * The todo write actions.
 *
 * Every change takes the same route as the other modules: apply it locally so
 * the screen updates at once, queue the mutation, and let the sync runner replay
 * it. Reordering is a single-row write — only the moved todo's `position`
 * changes — so two phones dragging different todos never conflict.
 *
 * These are full-row upserts (the agenda pattern), not the groceries rapid-tap
 * machinery: a todo is toggled or moved by a deliberate tap, not the
 * millisecond-apart double-tap that the `intended`/`readRow` dance exists for.
 */

export interface TodoDraft {
  title: string;
  assigned_to: string | null;
  scheduled_at: string | null;
  recurrence: TodoRecurrence | null;
  category: string | null;
}

async function writeTodo(record: TodoRecord): Promise<void> {
  const stamped: TodoRecord = { ...record, updated_at: stamp(), deleted_at: record.deleted_at ?? null };
  const mutation = { op: 'upsert', table: 'todos', id: newId(), row: stamped } as unknown as TodoMutation;
  await tdb.transaction('rw', [tdb.todos, tdb.outbox], async () => {
    await tdb.todos.put(stamped);
    await tdb.outbox.add({ mutation, queued_at: new Date().toISOString() });
  });
  void todoSync.sync();
}

/** Append one entry to a todo's little activity log (log.ts, migration 032). */
function withLog(
  record: TodoRecord,
  action: TodoLogAction,
  by: string | null,
  detail?: string | null,
): TodoRecord {
  return { ...record, log: appendLog(record.log, logEntry(action, by, detail)) };
}

/** Add a new todo at the top of the open list (position just below the smallest). */
export async function addTodo(draft: TodoDraft, createdBy: string | null): Promise<void> {
  const top = await tdb.todos.orderBy('position').filter((t) => !t.deleted_at).first();
  const position = top ? top.position - 1 : 0;
  await writeTodo(
    withLog(
      {
        id: newId(),
        title: draft.title,
        done: false,
        assigned_to: draft.assigned_to,
        scheduled_at: draft.scheduled_at,
        recurrence: draft.recurrence,
        category: draft.category,
        log: null,
        position,
        created_by: createdBy,
        updated_at: '', // stamped in writeTodo
        deleted_at: null,
      },
      'created',
      createdBy,
    ),
  );
}

/** The Dutch label each editable field logs under, when it changes. */
const FIELD_LABELS: Record<string, string> = {
  title: 'titel',
  assigned_to: 'wie',
  scheduled_at: 'herinnering',
  recurrence: 'herhaling',
  category: 'categorie',
};

/**
 * Edit an existing todo's fields (title / assignee / schedule / repeat /
 * category). Logs a single `edited` entry naming the aspects that actually
 * changed; a save that changes nothing is a no-op (no write, no log entry).
 */
export async function updateTodo(
  id: string,
  patch: Partial<Pick<TodoRecord, 'title' | 'assigned_to' | 'scheduled_at' | 'recurrence' | 'category'>>,
  by: string | null = null,
): Promise<void> {
  const existing = await tdb.todos.get(id);
  if (!existing) return;
  const changed = (Object.keys(patch) as (keyof typeof patch)[]).filter(
    (k) => JSON.stringify(patch[k] ?? null) !== JSON.stringify(existing[k] ?? null),
  );
  if (changed.length === 0) return;
  const detail = changed.map((k) => FIELD_LABELS[k] ?? k).join(', ');
  await writeTodo(withLog({ ...existing, ...patch }, 'edited', by, detail));
}

/**
 * Whether a todo belongs on the **active** (open) list right now, as opposed to
 * its category's "Klaar" section.
 *
 * - A one-off is open until it is ticked done.
 * - A recurring todo is open while `done = false`, and — once completed — again
 *   from the **day of its next deadline** onward: completing it marks it done and
 *   rolls `scheduled_at` forward, so it sits in "Klaar" until that day arrives,
 *   then reappears (TODOS.md §3). The stored `done` never changes on its own; the
 *   reappearance is derived here, so it works offline with no background flip.
 */
export function isTodoOpen(
  t: Pick<TodoRecord, 'done' | 'recurrence' | 'scheduled_at'>,
): boolean {
  if (!t.recurrence) return !t.done;
  if (!t.done) return true;
  return t.scheduled_at ? isDueReached(t.scheduled_at, new Date().toISOString()) : true;
}

/**
 * How a todo should read on the screen, in three states rather than the old
 * open/done split:
 *   - `open`    — still to do (in the active list).
 *   - `waiting` — a recurring todo completed for this cycle, resting until the
 *     day of its next deadline. It is *not* finished, so it is not struck through
 *     in "Klaar"; it shows its next date and sits, dimmed, under the active list.
 *   - `done`    — a one-off that was ticked off (goes to the collapsed "Klaar").
 * A recurring todo is therefore never in the permanent done pile — it is always
 * either active or waiting for its next turn (see {@link isTodoOpen}).
 */
export function todoState(
  t: Pick<TodoRecord, 'done' | 'recurrence' | 'scheduled_at'>,
): 'open' | 'waiting' | 'done' {
  if (isTodoOpen(t)) return 'open';
  return t.recurrence ? 'waiting' : 'done';
}

/**
 * Tick / untick.
 *
 * A one-off simply flips `done`. A **recurring** todo instead moves between the
 * active list and its category's "Klaar" section:
 *   - ticking one that is *open* completes this occurrence — marks it done and
 *     rolls `scheduled_at` forward to the next date, so it drops into "Klaar"
 *     until that day (see {@link isTodoOpen}); every other field, the category
 *     included, is carried by the spread so it reinstates into the same group;
 *   - unticking one that is *waiting* in "Klaar" (done, next day not yet here)
 *     brings it straight back onto the active list, leaving the schedule alone.
 * A recurring todo always carries a `scheduled_at`, but guard for it defensively.
 */
export async function toggleDone(id: string, by: string | null = null): Promise<void> {
  const existing = await tdb.todos.get(id);
  if (!existing) return;
  if (existing.recurrence && existing.scheduled_at) {
    if (isTodoOpen(existing)) {
      const scheduled_at = nextDue(existing.recurrence, existing.scheduled_at, new Date().toISOString());
      await writeTodo(withLog({ ...existing, done: true, scheduled_at }, 'recurred', by));
    } else {
      await writeTodo(withLog({ ...existing, done: false }, 'reopened', by));
    }
    return;
  }
  const done = !existing.done;
  await writeTodo(withLog({ ...existing, done }, done ? 'completed' : 'reopened', by));
}

/**
 * Move a todo one step within an ordered list. `ordered` is the currently
 * displayed sequence (ascending by position); the new position lands the row on
 * the far side of its neighbour — the average of the neighbour and the row
 * beyond it, or a step past the end.
 */
export async function moveTodo(ordered: TodoRecord[], id: string, dir: -1 | 1): Promise<void> {
  const i = ordered.findIndex((t) => t.id === id);
  if (i < 0) return;
  const j = i + dir;
  if (j < 0 || j >= ordered.length) return;
  const neighbour = ordered[j]!;
  const beyond = ordered[j + dir];
  const position = beyond ? (neighbour.position + beyond.position) / 2 : neighbour.position + dir;
  await writeTodo({ ...ordered[i]!, position });
}

/**
 * Drop a todo at a new slot in its group (press-and-hold drag). `ordered` is the
 * group's current sequence (ascending by position); `slot` is the insertion index
 * among the *other* rows (0…rest.length). Lands the row between its new
 * neighbours — one single-row float write, like `moveTodo`, so two phones
 * dragging different todos never conflict. A drop back onto its own slot is a
 * no-op (no write, no history churn).
 */
export async function moveTodoTo(ordered: TodoRecord[], id: string, slot: number): Promise<void> {
  const d = ordered.findIndex((t) => t.id === id);
  if (d < 0) return;
  const rest = ordered.filter((t) => t.id !== id);
  if (rest.length === 0) return;
  const k = Math.max(0, Math.min(slot, rest.length));
  if (k === d) return; // back where it started
  const before = rest[k - 1];
  const after = rest[k];
  const position = !before
    ? after!.position - 1 // to the top
    : !after
      ? before.position + 1 // to the bottom
      : (before.position + after.position) / 2; // between neighbours
  await writeTodo({ ...ordered[d]!, position });
}

/**
 * Rename a category everywhere it is used. A category is just a string on each
 * todo, so renaming is a bulk edit of every todo carrying the old label — each a
 * normal LWW upsert through the outbox. Empty/duplicate targets are a no-op.
 */
export async function renameCategory(from: string, to: string, by: string | null = null): Promise<void> {
  const target = to.trim();
  if (!target || target === from) return;
  const rows = await tdb.todos.filter((t) => !t.deleted_at && t.category === from).toArray();
  for (const row of rows) await writeTodo(withLog({ ...row, category: target }, 'edited', by, 'categorie'));
}

/** Remove a category: clear it from every todo that carries it (the todos stay). */
export async function deleteCategory(name: string, by: string | null = null): Promise<void> {
  const rows = await tdb.todos.filter((t) => !t.deleted_at && t.category === name).toArray();
  for (const row of rows) await writeTodo(withLog({ ...row, category: null }, 'edited', by, 'categorie'));
}

/** Soft delete — the tombstone propagates to the other phone. */
export async function deleteTodo(id: string): Promise<void> {
  const updated_at = stamp();
  const mutation = {
    op: 'delete',
    table: 'todos',
    id: newId(),
    row: { id, updated_at },
  } as unknown as TodoMutation;
  await tdb.transaction('rw', [tdb.todos, tdb.outbox], async () => {
    const existing = await tdb.todos.get(id);
    if (existing) {
      await tdb.todos.put({ ...existing, deleted_at: existing.deleted_at ?? updated_at, updated_at });
    }
    await tdb.outbox.add({ mutation, queued_at: new Date().toISOString() });
  });
  void todoSync.sync();
}
